import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import ModalMC from '../../../components/ModalMC';
import DropdownMC from '../../../components/DropdownMC';
import useConversationTopicsModalData from '../../../hooks/inbox/data/useConversationTopicsModalData';
import useConversationTopicsModalActions from '../../../hooks/inbox/actions/useConversationTopicsModalActions';
import useConversationTopicsModalUI from '../../../hooks/inbox/ui/useConversationTopicsModalUI';

/**
 * Componente Organismo para asignar tópicos y sub-tópicos a una conversación.
 * Separa lógica de negocio en hooks especializados y optimiza performance con useMemo/useCallback.
 *
 * @component ConversationTopicsModalMC
 * @category Component - Organism
 * @since 3.1.0
 * @version 3.1.1 - Refactor separación lógica y optimización performance
 */
const ConversationTopicsModalMC = ({ customerId, platform, chatId, toggleModal }) => {
  const { t } = useTranslation('InboxMC');

  // Data Layer: carga de tópicos y estado
  const {
    topicOptions,
    subtopicOptions,
    selectedTopic,
    setSelectedTopic,
    selectedSubtopic,
    setSelectedSubtopic,
    hasEnabledSubmit
  } = useConversationTopicsModalData();


  // UI Layer: handlers de selección
  const { handleSelectTopic, handleSelectSubtopic } = useConversationTopicsModalUI({
    setSelectedTopic,
    setSelectedSubtopic
  });

  // Debug tracking para monitoreo de performance y optimización (v3.1.1)
  useTraceUpdate('ConversationTopicsModalMC', {
    topicOptionsLength: topicOptions.length,
    subtopicOptionsLength: subtopicOptions.length,
    selectedTopic,
    selectedSubtopic,
    hasEnabledSubmit,
    customerId,
    platform,
    chatId
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  // Actions Layer: handler de guardado
  const { handleSave } = useConversationTopicsModalActions({
    customerId,
    platform,
    chatId,
    toggleModal
  });

  // Botón de acción
  const buttons = useMemo(() => ([
    {
      label: t('ConversationTopics:conversation_topics.messages_modal.assign_topic'),
      onClick: () => handleSave({ selectedTopic, selectedSubtopic }),
      className: 'tw-w-full',
      disabled: !hasEnabledSubmit
    }
  ]), [handleSave, t, hasEnabledSubmit, selectedTopic, selectedSubtopic]);

  return (
    <ModalMC
      visible
      onClose={toggleModal}
      title={t('ConversationTopics:conversation_topics.messages_modal.add_topic')}
      containerClassName="!tw-max-w-2xl"
      className="tw-fixed tw-top-0 tw-right-0 tw-w-1/3 tw-h-full !tw-rounded-none tw-flex tw-flex-col !tw-p-0"
      headerClassName="tw-w-full tw-border-b tw-border-gray-200 tw-p-4"
      footerClassName="tw-w-full tw-border-t tw-border-gray-200 tw-p-4"
      buttons={buttons}
    >
      <div className="tw-flex-1 tw-overflow-auto tw-p-4">
        <DropdownMC
          useFloatingLabel
          options={topicOptions}
          label={t('ConversationTopics:conversation_topics.messages_modal.topic')}
          defaultValue={selectedTopic || ''}
          className="tw-mb-3"
          placeholder={t('ConversationTopics:conversation_topics.messages_modal.select_topic')}
          onSelect={handleSelectTopic}
        />
        {subtopicOptions?.length > 0 && (
          <DropdownMC
            useFloatingLabel
            options={subtopicOptions}
            label={t('ConversationTopics:conversation_topics.messages_modal.subtopic')}
            defaultValue={selectedSubtopic || ''}
            className="tw-mb-3"
            placeholder={t('ConversationTopics:conversation_topics.messages_modal.select_subtopic')}
            onSelect={handleSelectSubtopic}
          />
        )}
      </div>
    </ModalMC>
  );
};

export default React.memo(ConversationTopicsModalMC);
